import { match, P } from "ts-pattern";
import { KeyValueList } from "../../key-value-list";
import { CollateralMetrics } from "./types";
import { TotalApr } from "./total-apr";
import { FutureProjections } from "./future-projections";
import { BreakdownCollateral } from "../../apr-display-v2/breakdown/breakdown-collateral";
import { DepositProjectedAprDescription } from "../descriptions";
import { cn } from "../../../utils/class-names";
import { getUtilizationClassName } from "../utils";

interface ProjectedAprProps {
  currentMetrics: CollateralMetrics;
  newMetrics: CollateralMetrics | null;
}

export function ProjectedApr({
  currentMetrics,
  newMetrics,
}: ProjectedAprProps): JSX.Element {
  return (
    <KeyValueList.Pair
      description={<DepositProjectedAprDescription />}
      trailingContent={
        newMetrics ? <TrailingContent newMetrics={newMetrics} /> : undefined
      }
      trailingContentContainerClassName="gap-0"
    >
      <KeyValueList.Key>Projected APR</KeyValueList.Key>

      <KeyValueList.Value>
        {match({ currentMetrics, newMetrics })
          .with(
            {
              newMetrics: P.nonNullable,
            },
            ({
              newMetrics: {
                totalCollateralApr,
                hasCollateralRewards,
                utilizationState,
              },
            }) => (
              <TotalApr
                totalCollateralApr={totalCollateralApr}
                hasCollateralRewards={hasCollateralRewards}
                className={cn(
                  hasCollateralRewards ? "text-2xl" : "text-base",
                  getUtilizationClassName(utilizationState)
                )}
              />
            )
          )
          .with(
            {
              currentMetrics: {
                projectionsState: {
                  _tag: P.union("increasing", "decreasing"),
                },
              },
            },
            ({
              currentMetrics: {
                totalCollateralRewardsApr,
                projectionsState: { baseApr1d, baseApr3d },
                utilizationState,
              },
            }) => (
              <FutureProjections
                totalCollateralRewardsApr={totalCollateralRewardsApr}
                collateralBaseApr1d={baseApr1d}
                collateralBaseApr3d={baseApr3d}
                utilizationState={utilizationState}
              />
            )
          )
          .otherwise(() => "--")}
      </KeyValueList.Value>
    </KeyValueList.Pair>
  );
}

interface TrailingContentProps {
  newMetrics: CollateralMetrics;
}

function TrailingContent({
  newMetrics,
}: TrailingContentProps): JSX.Element | null {
  const {
    hasCollateralRewards,
    projectionsState,
    collateralRewards,
    collateralBaseApr,
    totalCollateralRewardsApr,
    utilizationState,
  } = newMetrics;

  const hasTrailingContent =
    hasCollateralRewards || projectionsState._tag !== "stable";

  if (!hasTrailingContent) return null;

  return (
    <div className="flex flex-col gap-5">
      {hasCollateralRewards && (
        <BreakdownCollateral
          className="gap-0 text-sm"
          collateralRewards={collateralRewards}
          collateralBaseApr={collateralBaseApr}
          align="right"
        />
      )}

      {match(projectionsState)
        .with(
          { _tag: P.union("increasing", "decreasing") },
          ({ baseApr1d, baseApr3d }) => (
            <FutureProjections
              totalCollateralRewardsApr={totalCollateralRewardsApr}
              collateralBaseApr1d={baseApr1d}
              collateralBaseApr3d={baseApr3d}
              utilizationState={utilizationState}
            />
          )
        )
        .otherwise(() => null)}
    </div>
  );
}
